import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import DailyGoalProgress from '../DailyGoalProgress';
import { 
  TrendingUp, TrendingDown, Euro, ShoppingCart, Users, Clock,
  Package, Truck, Store, Utensils, ArrowUpRight, ArrowDownRight,
  CheckCircle2, AlertCircle, Timer
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function DashboardOverview({ sessionToken, onOpenDeliveryPickup }) {
  const [stats, setStats] = useState({
    todayRevenue: 0,
    todayOrders: 0,
    avgOrderValue: 0,
    activeOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantSettings, setTenantSettings] = useState(null);

  // Fetch tenant settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!sessionToken) return;
      try {
        const response = await fetch(`${API_BASE}/api/tenant-settings`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTenantSettings(data);
        }
      } catch (error) {
        console.error('Error loading tenant settings:', error);
      }
    };
    fetchSettings();
  }, [sessionToken]);

  // Check if feature is enabled
  const isFeatureEnabled = (featureKey) => {
    if (!tenantSettings?.feature_toggles) return true;
    return tenantSettings.feature_toggles[featureKey] !== false;
  };

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      // Fetch active orders for stats
      const ordersRes = await fetch(`${API_BASE}/api/cashx/kasse/active-orders`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const allOrders = [...(data.new || []), ...(data.processing || [])];
        
        // Calculate stats
        const today = new Date().toDateString();
        const todayOrders = allOrders.filter(o => 
          new Date(o.created_at).toDateString() === today
        );
        
        const todayRevenue = todayOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        const avgOrderValue = todayOrders.length > 0 ? todayRevenue / todayOrders.length : 0;
        
        setStats({
          todayRevenue,
          todayOrders: todayOrders.length,
          avgOrderValue,
          activeOrders: allOrders.length,
          completedOrders: data.completed?.total || 0,
          cancelledOrders: 0
        });
        
        setRecentOrders(allOrders.slice(0, 8));
        
        // Calculate top products
        const productCounts = {};
        allOrders.forEach(order => {
          (order.items || []).forEach(item => {
            const name = item.name || 'Unbekannt';
            productCounts[name] = (productCounts[name] || 0) + (item.quantity || 1);
          });
        });
        
        const sorted = Object.entries(productCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, count]) => ({ name, count }));
        
        setTopProducts(sorted);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Get order type icon
  const getOrderTypeIcon = (type) => {
    switch (type) {
      case 'delivery': return <Truck className="w-4 h-4" />;
      case 'pickup': return <Package className="w-4 h-4" />;
      case 'dine_in': return <Utensils className="w-4 h-4" />;
      default: return <Store className="w-4 h-4" />;
    }
  };

  // Get time since order
  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 1) return 'Gerade eben';
    if (diff < 60) return `${diff} Min.`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-400">Lade Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400">Übersicht für heute, {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
      </div>

      {/* Quick Actions: Tables & Delivery/Pickup */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Tables Section - Only show if table_service_enabled */}
        {isFeatureEnabled('table_service_enabled') && (
          <Card className="bg-[#12121a] border-gray-800 lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Utensils className="w-5 h-5 text-amber-400" />
                Tische
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((tableNum) => (
                  <button
                    key={tableNum}
                    className="aspect-square rounded-lg border-2 border-gray-700 hover:border-amber-500 hover:bg-amber-500/10 
                             flex flex-col items-center justify-center transition-all group"
                  >
                    <span className="text-lg font-bold text-white group-hover:text-amber-400">{tableNum}</span>
                    <span className="text-[10px] text-gray-500">Frei</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery/Pickup Section - MODERNIZED */}
        <Card className={`bg-gradient-to-br from-[#111C32] to-[#0E1628] border-slate-700/50 shadow-xl ${!isFeatureEnabled('table_service_enabled') ? 'lg:col-span-3' : ''}`}>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-teal-400" />
              </div>
              Neue Bestellung
            </CardTitle>
          </CardHeader>
          <CardContent className={`space-y-3 ${!isFeatureEnabled('table_service_enabled') ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : ''}`} style={!isFeatureEnabled('table_service_enabled') ? {display: 'grid'} : {}}>
            {/* Abholung - Only show if pickup_enabled */}
            {isFeatureEnabled('pickup_enabled') && (
              <button
                onClick={() => onOpenDeliveryPickup && onOpenDeliveryPickup('pickup')}
                className="w-full p-5 rounded-xl border-2 border-slate-700/60 hover:border-emerald-500 
                         bg-[#1A2642]/40 hover:bg-emerald-500/10 
                         flex items-center gap-4 transition-all duration-200 group
                         hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/10
                         active:scale-[0.98] min-h-[80px]"
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center 
                              group-hover:scale-110 group-hover:bg-emerald-500/30 transition-all duration-200
                              group-hover:shadow-lg group-hover:shadow-emerald-500/20">
                  <Store className="w-7 h-7 text-emerald-400" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Abholung
                  </h3>
                  <p className="text-slate-400 text-sm mt-0.5">Kunde holt selbst ab</p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                  </div>
                </div>
              </button>
            )}

            {/* Lieferung - Only show if delivery_enabled */}
            {isFeatureEnabled('delivery_enabled') && (
              <button
                onClick={() => onOpenDeliveryPickup && onOpenDeliveryPickup('delivery')}
                className="w-full p-5 rounded-xl border-2 border-slate-700/60 hover:border-blue-500 
                         bg-[#1A2642]/40 hover:bg-blue-500/10 
                         flex items-center gap-4 transition-all duration-200 group
                         hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/10
                         active:scale-[0.98] min-h-[80px]"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center 
                              group-hover:scale-110 group-hover:bg-blue-500/30 transition-all duration-200
                              group-hover:shadow-lg group-hover:shadow-blue-500/20">
                  <Truck className="w-7 h-7 text-blue-400" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Lieferung
                  </h3>
                  <p className="text-slate-400 text-sm mt-0.5">Wir liefern zum Kunden</p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <ArrowUpRight className="w-4 h-4 text-blue-400" />
                  </div>
                </div>
              </button>
            )}

            {/* Tisch - Only show if table_service_enabled */}
            {isFeatureEnabled('table_service_enabled') && (
              <button
                onClick={() => onOpenDeliveryPickup && onOpenDeliveryPickup('table')}
                className="w-full p-5 rounded-xl border-2 border-slate-700/60 hover:border-amber-500 
                         bg-[#1A2642]/40 hover:bg-amber-500/10 
                         flex items-center gap-4 transition-all duration-200 group
                         hover:-translate-y-1 hover:shadow-lg hover:shadow-amber-500/10
                         active:scale-[0.98] min-h-[80px]"
              >
                <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center 
                              group-hover:scale-110 group-hover:bg-amber-500/30 transition-all duration-200
                              group-hover:shadow-lg group-hover:shadow-amber-500/20">
                  <Utensils className="w-7 h-7 text-amber-400" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Tisch
                  </h3>
                  <p className="text-slate-400 text-sm mt-0.5">Bestellung am Tisch</p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <ArrowUpRight className="w-4 h-4 text-amber-400" />
                  </div>
                </div>
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today's Revenue */}
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-400/70 text-sm font-medium">Tagesumsatz</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.todayRevenue.toFixed(2)} €
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Euro className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowUpRight className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400">+12%</span>
              <span className="text-gray-500">vs. gestern</span>
            </div>
          </CardContent>
        </Card>

        {/* Today's Orders */}
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400/70 text-sm font-medium">Bestellungen heute</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.todayOrders}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowUpRight className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400">+5</span>
              <span className="text-gray-500">vs. gestern</span>
            </div>
          </CardContent>
        </Card>

        {/* Average Order Value */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400/70 text-sm font-medium">Ø Bestellwert</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.avgOrderValue.toFixed(2)} €
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowUpRight className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400">+2.50 €</span>
              <span className="text-gray-500">vs. gestern</span>
            </div>
          </CardContent>
        </Card>

        {/* Active Orders */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-400/70 text-sm font-medium">Aktive Bestellungen</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.activeOrders}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-sm">
              <Badge className="bg-green-500/20 text-green-400 border-0">
                {stats.completedOrders} erledigt
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* FEATURE L: Daily Goal Progress */}
        <DailyGoalProgress sessionToken={sessionToken} />
        
        {/* Recent Orders */}
        <Card className="lg:col-span-2 bg-[#12121a] border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center justify-between">
              <span>Aktuelle Bestellungen</span>
              <Badge variant="outline" className="text-gray-400 border-gray-700">
                Live
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {recentOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Keine aktiven Bestellungen
                </div>
              ) : (
                <div className="space-y-3">
                  {recentOrders.map((order, idx) => (
                    <div 
                      key={order.order_id || idx}
                      className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800/80 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-10 h-10 rounded-lg flex items-center justify-center
                          ${order.delivery_type === 'delivery' ? 'bg-purple-500/20 text-purple-400' : ''}
                          ${order.delivery_type === 'pickup' ? 'bg-amber-500/20 text-amber-400' : ''}
                          ${order.delivery_type === 'dine_in' ? 'bg-blue-500/20 text-blue-400' : ''}
                          ${!order.delivery_type ? 'bg-gray-500/20 text-gray-400' : ''}
                        `}>
                          {getOrderTypeIcon(order.delivery_type)}
                        </div>
                        <div>
                          <div className="font-medium text-white">
                            #{order.order_number}
                          </div>
                          <div className="text-sm text-gray-400">
                            {order.items?.length || 0} Artikel • {getTimeSince(order.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-white">
                          {(order.total || 0).toFixed(2)} €
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`
                            ${order.status === 'new' ? 'text-amber-400 border-amber-500/30' : ''}
                            ${order.status === 'processing' ? 'text-blue-400 border-blue-500/30' : ''}
                          `}
                        >
                          {order.status === 'new' ? 'Neu' : 'In Arbeit'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="bg-[#12121a] border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white">Top Produkte</CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Noch keine Daten
              </div>
            ) : (
              <div className="space-y-4">
                {topProducts.map((product, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold
                        ${idx === 0 ? 'bg-amber-500/20 text-amber-400' : ''}
                        ${idx === 1 ? 'bg-gray-400/20 text-gray-300' : ''}
                        ${idx === 2 ? 'bg-orange-600/20 text-orange-400' : ''}
                        ${idx > 2 ? 'bg-gray-700/50 text-gray-400' : ''}
                      `}>
                        {idx + 1}
                      </div>
                      <span className="text-white truncate max-w-[150px]">{product.name}</span>
                    </div>
                    <Badge variant="outline" className="text-gray-400 border-gray-700">
                      {product.count}x
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#12121a] border-gray-800">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.completedOrders}</div>
              <div className="text-xs text-gray-500">Abgeschlossen</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-gray-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Timer className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.activeOrders}</div>
              <div className="text-xs text-gray-500">In Bearbeitung</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-gray-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Truck className="w-5 h-5 text-purple-400" />
            <div>
              <div className="text-2xl font-bold text-white">
                {recentOrders.filter(o => o.delivery_type === 'delivery').length}
              </div>
              <div className="text-xs text-gray-500">Lieferungen</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-gray-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-400" />
            <div>
              <div className="text-2xl font-bold text-white">
                {recentOrders.filter(o => o.delivery_type === 'dine_in').length}
              </div>
              <div className="text-xs text-gray-500">Im Haus</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
